import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { SizeString, useWindowSizeStore } from 'src/stores/useWindowSizeStore';
import useLayoutConfigSetting from 'src/hooks/useLayoutConfigSetting';
import { TRADE_PAGE_CHART_HEIGHT_KEY } from 'src/constants/trade';
import { GridConfigValue, GRID_CONFIG_VALUES, TRADE_AREAS } from './type';

export type { GridConfigValue } from './type';

interface LayoutResult {
	gridTemplateAreas: string;
	gridTemplateColumns: string;
	gridTemplateRows: string;
}

type SizeConfig = Partial<Record<SizeString, string | Record<string, string>>>;
type ConfigValue = string | SizeConfig;

function resolveConfig(
	config: ConfigValue,
	sizeKey: SizeString,
	variant: GridConfigValue
): string {
	if (typeof config === 'string') return config;

	const bySize = config[sizeKey];
	if (typeof bySize === 'string') return bySize;
	if (typeof bySize === 'object') return bySize[variant] ?? bySize['default'];

	return '1fr';
}

const CHART_HEIGHT_PLACEHOLDER = 'CHART_HEIGHT';
const MARKET_BAR_ROW_HEIGHT = '42px';
const MARKET_BAR_ROW_HEIGHT_SMALL = '32px';

// ── With orderbook (3-column desktop layouts) ──

const DESKTOP_AREA_TEMPLATES = {
	default: `
		"${TRADE_AREAS.MarketBar}      ${TRADE_AREAS.Orderbook}       ${TRADE_AREAS.TradeForm}"
		"${TRADE_AREAS.MarketSelector} ${TRADE_AREAS.Orderbook}       ${TRADE_AREAS.TradeForm}"
		"${TRADE_AREAS.Chart}          ${TRADE_AREAS.Orderbook}       ${TRADE_AREAS.TradeForm}"
		"${TRADE_AREAS.Table}          ${TRADE_AREAS.Table}           ${TRADE_AREAS.TradeForm}"
	`,
	mirrored: `
		"${TRADE_AREAS.TradeForm}      ${TRADE_AREAS.Orderbook}       ${TRADE_AREAS.MarketBar}"
		"${TRADE_AREAS.TradeForm}      ${TRADE_AREAS.Orderbook}       ${TRADE_AREAS.MarketSelector}"
		"${TRADE_AREAS.TradeForm}      ${TRADE_AREAS.Orderbook}       ${TRADE_AREAS.Chart}"
		"${TRADE_AREAS.TradeForm}      ${TRADE_AREAS.Table}           ${TRADE_AREAS.Table}"
	`,
	split: `
		"${TRADE_AREAS.Orderbook}      ${TRADE_AREAS.MarketBar}       ${TRADE_AREAS.TradeForm}"
		"${TRADE_AREAS.Orderbook}      ${TRADE_AREAS.MarketSelector}  ${TRADE_AREAS.TradeForm}"
		"${TRADE_AREAS.Orderbook}      ${TRADE_AREAS.Chart}           ${TRADE_AREAS.TradeForm}"
		"${TRADE_AREAS.Table}          ${TRADE_AREAS.Table}           ${TRADE_AREAS.TradeForm}"
	`,
	splitMirrored: `
		"${TRADE_AREAS.TradeForm}      ${TRADE_AREAS.MarketBar}       ${TRADE_AREAS.Orderbook}"
		"${TRADE_AREAS.TradeForm}      ${TRADE_AREAS.MarketSelector}  ${TRADE_AREAS.Orderbook}"
		"${TRADE_AREAS.TradeForm}      ${TRADE_AREAS.Chart}           ${TRADE_AREAS.Orderbook}"
		"${TRADE_AREAS.TradeForm}      ${TRADE_AREAS.Table}           ${TRADE_AREAS.Table}"
	`,
	vertical: `
        "${TRADE_AREAS.MarketBar}      ${TRADE_AREAS.MarketBar}"
        "${TRADE_AREAS.MarketSelector} ${TRADE_AREAS.MarketSelector}"
        "${TRADE_AREAS.Chart}           ${TRADE_AREAS.TradeForm}"
        "${TRADE_AREAS.Orderbook}       ${TRADE_AREAS.TradeForm}"
        "${TRADE_AREAS.Table}           ${TRADE_AREAS.Table}"
      `,
};

// ── Without orderbook (2-column desktop layouts) ──

const DESKTOP_AREA_TEMPLATES_NO_OB = {
	default: `
		"${TRADE_AREAS.MarketBar}      ${TRADE_AREAS.TradeForm}"
		"${TRADE_AREAS.MarketSelector} ${TRADE_AREAS.TradeForm}"
		"${TRADE_AREAS.Chart}          ${TRADE_AREAS.TradeForm}"
		"${TRADE_AREAS.Table}          ${TRADE_AREAS.TradeForm}"
	`,
	mirrored: `
		"${TRADE_AREAS.TradeForm}      ${TRADE_AREAS.MarketBar}"
		"${TRADE_AREAS.TradeForm}      ${TRADE_AREAS.MarketSelector}"
		"${TRADE_AREAS.TradeForm}      ${TRADE_AREAS.Chart}"
		"${TRADE_AREAS.TradeForm}      ${TRADE_AREAS.Table}"
	`,
	vertical: `
        "${TRADE_AREAS.MarketBar}      ${TRADE_AREAS.MarketBar}"
        "${TRADE_AREAS.MarketSelector} ${TRADE_AREAS.MarketSelector}"
        "${TRADE_AREAS.Chart}           ${TRADE_AREAS.TradeForm}"
		"${TRADE_AREAS.Chart}           ${TRADE_AREAS.TradeForm}"
        "${TRADE_AREAS.Table}           ${TRADE_AREAS.Table}"
      `,
};

const AREA_TEMPLATES = {
	horizontal: {
		'2xl': DESKTOP_AREA_TEMPLATES,
		xl: DESKTOP_AREA_TEMPLATES,
		lg: DESKTOP_AREA_TEMPLATES,
		md: DESKTOP_AREA_TEMPLATES,
		sm: DESKTOP_AREA_TEMPLATES.vertical,
	},
	vertical: DESKTOP_AREA_TEMPLATES.vertical,
	mobile: `
      "${TRADE_AREAS.MarketBar}"
      "${TRADE_AREAS.MarketSelector}"
      "${TRADE_AREAS.Chart}"
      "${TRADE_AREAS.Orderbook}"
      "${TRADE_AREAS.TradeForm}"
      "${TRADE_AREAS.Table}"
    `,
};

const AREA_TEMPLATES_NO_OB = {
	horizontal: {
		'2xl': DESKTOP_AREA_TEMPLATES_NO_OB,
		xl: DESKTOP_AREA_TEMPLATES_NO_OB,
		lg: DESKTOP_AREA_TEMPLATES_NO_OB,
		md: DESKTOP_AREA_TEMPLATES_NO_OB,
		sm: DESKTOP_AREA_TEMPLATES_NO_OB.vertical,
	},
	vertical: DESKTOP_AREA_TEMPLATES_NO_OB.vertical,
	mobile: AREA_TEMPLATES.mobile,
};

// ── Column configs ──

const DESKTOP_COLUMN_CONFIG = {
	default: 'minmax(0, 1fr) 16.66% 20%',
	mirrored: '20% 16.66% 1fr',
	split: '16.66% minmax(0, 1fr) 20%',
	splitMirrored: '20% minmax(0, 1fr) 16.66%',
};

const LG_COLUMN_CONFIG = {
	default: 'minmax(0, 1fr) 280px 320px',
	mirrored: '320px 280px minmax(0, 1fr)',
	split: '280px minmax(0, 1fr) 320px',
	splitMirrored: '320px minmax(0, 1fr) 280px',
};

const MD_COLUMN_CONFIG = {
	default: 'minmax(0, 1fr) 260px 300px',
	mirrored: '300px 260px minmax(0, 1fr)',
	split: '260px minmax(0, 1fr) 300px',
	splitMirrored: '300px minmax(0, 1fr) 260px',
};

const COLUMN_CONFIG = {
	horizontal: {
		'2xl': {
			default: 'minmax(0, 1fr) 426px 512px',
			mirrored: '512px 426px minmax(0, 1fr)',
			split: '426px minmax(0, 1fr) 512px',
			splitMirrored: '512px minmax(0, 1fr) 426px',
		},
		xl: DESKTOP_COLUMN_CONFIG,
		lg: LG_COLUMN_CONFIG,
		md: MD_COLUMN_CONFIG,
		sm: '1fr 33%',
	},
	vertical: '1fr 33%',
	mobile: '1fr',
};

const DESKTOP_COLUMN_CONFIG_NO_OB = {
	default: 'minmax(0, 1fr) 20%',
	mirrored: '20% minmax(0, 1fr)',
};

const LG_COLUMN_CONFIG_NO_OB = {
	default: 'minmax(0, 1fr) 320px',
	mirrored: '320px minmax(0, 1fr)',
};

const MD_COLUMN_CONFIG_NO_OB = {
	default: 'minmax(0, 1fr) 300px',
	mirrored: '300px minmax(0, 1fr)',
};

const COLUMN_CONFIG_NO_OB = {
	horizontal: {
		'2xl': {
			default: 'minmax(0, 1fr) 512px',
			mirrored: '512px minmax(0, 1fr)',
		},
		xl: DESKTOP_COLUMN_CONFIG_NO_OB,
		lg: LG_COLUMN_CONFIG_NO_OB,
		md: MD_COLUMN_CONFIG_NO_OB,
		sm: '1fr 33%',
	},
	vertical: '1fr 33%',
	mobile: '1fr',
};

// ── Row configs ──

const DESKTOP_ROW_CONFIG = `${MARKET_BAR_ROW_HEIGHT} 68px ${CHART_HEIGHT_PLACEHOLDER} minmax(500px, 30vh)`;
const SMALL_ROW_CONFIG = `${MARKET_BAR_ROW_HEIGHT_SMALL} 56px 40vh minmax(500px, 30vh) minmax(500px, 30vh)`;
const SMALL_ROW_CONFIG_NO_OB = `${MARKET_BAR_ROW_HEIGHT_SMALL} 56px minmax(500px, 60vh) minmax(300px, 30vh) minmax(500px, 30vh)`;

const ROW_CONFIG = {
	horizontal: {
		'2xl': DESKTOP_ROW_CONFIG,
		xl: DESKTOP_ROW_CONFIG,
		lg: DESKTOP_ROW_CONFIG,
		md: DESKTOP_ROW_CONFIG,
		sm: SMALL_ROW_CONFIG,
	},
	vertical: SMALL_ROW_CONFIG,
	mobile: 'auto auto auto auto auto auto',
};

const ROW_CONFIG_NO_OB = {
	horizontal: {
		'2xl': DESKTOP_ROW_CONFIG,
		xl: DESKTOP_ROW_CONFIG,
		lg: DESKTOP_ROW_CONFIG,
		md: DESKTOP_ROW_CONFIG,
		sm: SMALL_ROW_CONFIG_NO_OB,
	},
	vertical: SMALL_ROW_CONFIG_NO_OB,
	mobile: ROW_CONFIG.mobile,
};

// ── Helpers ──

const NO_OB_VARIANT_MAP: Partial<Record<GridConfigValue, GridConfigValue>> = {
	split: 'default',
	splitMirrored: 'mirrored',
};

function getVariant(
	layoutSetting: GridConfigValue | undefined,
	showOrderbook: boolean
): GridConfigValue {
	let variant: GridConfigValue;
	if (!layoutSetting || layoutSetting === 'defaultTablet') {
		variant = 'default';
	} else if (GRID_CONFIG_VALUES.includes(layoutSetting)) {
		variant = layoutSetting;
	} else {
		variant = 'default';
	}

	if (!showOrderbook) {
		return NO_OB_VARIANT_MAP[variant] ?? variant;
	}

	return variant;
}

export const useTradePageLayout = (
	showMarketBar = true,
	showOrderbook = true
): LayoutResult => {
	const { width, height, size } = useWindowSizeStore((s) => ({
		width: s.realtimeWidth,
		height: s.realtimeHeight,
		size: s.size,
	}));
	const [layoutSetting] = useLayoutConfigSetting();
	const [tradePageChartHeight] = useSyncLocalStorage<string>(
		TRADE_PAGE_CHART_HEIGHT_KEY
	);

	const isMobile = size === 'xs';
	const isVertical = height > width;
	const layoutType = isMobile
		? 'mobile'
		: isVertical
			? 'vertical'
			: 'horizontal';

	const variant = getVariant(layoutSetting, showOrderbook);

	const areaTemplates =
		showOrderbook || isMobile ? AREA_TEMPLATES : AREA_TEMPLATES_NO_OB;
	const columnConfig =
		showOrderbook || isMobile ? COLUMN_CONFIG : COLUMN_CONFIG_NO_OB;
	const rowConfig = showOrderbook || isMobile ? ROW_CONFIG : ROW_CONFIG_NO_OB;

	const areas = resolveConfig(areaTemplates[layoutType], size, variant);
	const columns = resolveConfig(columnConfig[layoutType], size, variant);

	let rows: string;
	const isLargeScreen =
		size === 'md' || size === 'lg' || size === 'xl' || size === '2xl';
	if (layoutType === 'horizontal' && isLargeScreen) {
		const defaultChartHeight = showOrderbook ? 'calc(75vh - 110px)' : '1fr';
		const chartHeight = tradePageChartHeight
			? `${tradePageChartHeight}px`
			: defaultChartHeight;
		const baseRowConfig = DESKTOP_ROW_CONFIG.replace(
			CHART_HEIGHT_PLACEHOLDER,
			chartHeight
		);
		rows = baseRowConfig;
	} else {
		rows = resolveConfig(rowConfig[layoutType], size, variant);
	}

	if (!showMarketBar) {
		rows = rows
			.replace(MARKET_BAR_ROW_HEIGHT, '0px')
			.replace(MARKET_BAR_ROW_HEIGHT_SMALL, '0px');
	}

	return {
		gridTemplateAreas: areas,
		gridTemplateColumns: columns,
		gridTemplateRows: rows,
	};
};
